import React, { PureComponent, ChangeEvent, SyntheticEvent } from 'react';
import { withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { compose } from 'react-recompose';
import { push } from 'redux-first-history';

import {
    CustomTable,
    SearchInput,
    Pagination,
    EmptyState,
    FiltersPopover,
    FilterIcon,
    MobileTable,
    SimpleLoader,
    palette,
    CustomTableHeadersProps,
    TertiaryButton,
    ToggleButtonGroup,
    Typography,
    Paper,
} from '@dev/ee-components';
import { safelyEncodeBase64Url } from '@dev/ee-tools';

import styled from 'styled-components';
import { Container } from '@material-ui/core';
import Skeleton from 'react-loading-skeleton';
import { Visibility as VisibilityIcon } from '@material-ui/icons';

import { parse } from 'query-string';
import { isEmpty } from 'lodash-es';

import { ShowDialog, HideDialog } from '@api/store/dialog/actions';
import { ShowDialogParams } from '@api/store/dialog/types';
import ExportModal from '../../ExportModal';
import { LoadChannelsStart, ExportChannels, FindChannels, ClearChannelsScreen } from '@api/store/channels/actions';
import { ExportChannelsParams } from '@api/services/ElasticApi';
import { ChannelsPayload } from '@api/store/channels/types';
import { ApplicationState as State } from '@api/store';
import ChannelsProps, { ChannelsState, ChannelsToggleBtnValues, ChannelsToggleBtnOptInterface } from './ChannelsInterface';
import { AccessLevel, CompressionFormat, ExportFileFormats } from '@dev/ee-api/ee-api-models';
import { parseUrlParams } from '@api/services/tools/handleUrlParams';
import { withRouter } from 'react-router-dom';
import { StatisticsPaths, pathsParams } from '@api/services/dictionaries/paths';

const mapState = (state: State, ownProps: ChannelsProps): ChannelsProps => {
    return {
        ...ownProps,
        browser: state.browser,
        channels: state.channels,
        account: state.account,
    };
};

const dispatchProps = {
    LoadChannelsStart,
    FindChannels,
    ShowDialog,
    HideDialog,
    ExportChannels,
    Push: push,
    ClearChannelsScreen,
};

type DispatchProps = {
    LoadChannelsStart(params?: ChannelsPayload);
    FindChannels(params?: ChannelsPayload);
    ShowDialog: (params: ShowDialogParams) => void;
    HideDialog: () => void;
    ExportChannels: (params: ExportChannelsParams) => void;
    Push(url: string): void;
    ClearChannelsScreen: () => void;
};

type Props = DispatchProps & ChannelsProps;

const ToggleButtonWrapper = styled.div`
    display: flex;
    justify-content: flex-end;
    background-color: inherit;
    margin-bottom: 24px;
`;

const Content = styled.div<{ isMobile?: boolean }>`
    padding: 30px 0 0 0;
    background-color: #fff;
    margin-top: 32px;
    border-radius: 4px;
    border: 1px solid ${palette.gray[3]};
`;

const TableContainer = styled.div`
    margin: auto;
    width: 100%;
`;

const ChannelsWrapper = styled.div`
    width: 100%;
    margin-top: 32px;
`;

const Header = styled.div`
    display: flex;
    margin-bottom: 20px;
    padding: 0px 50px 0 45px;
    justify-content: space-between;
`;

const LoadingWrapper = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 96px;
    width: 100%;
`;

const MobileHeader = styled.div`
    display: flex;
    margin-bottom: 20px;
    justify-content: space-between;
`;

const MobileTableContainer = styled.div`
    margin-top: 24px;
`;

const FiltersContainer = styled.div`
    background-color: #fff;
    border-radius: 4px;
    border: 1px solid ${palette.gray[3]};
    margin-left: 8px;
    padding: 11px 11px;
`;

const SearchContainer = styled.div`
    width: 278px;
`;

const MobileSearchContainer = styled.div`
    width: 100%;
    & > div {
        border: 1px solid ${palette.gray[3]};
    }
`;

const MobileFooter = styled.div`
    background: ${palette.basic.paper};
`;

const StyledVisibilityIcon = styled.div`
    display: flex;
    justify-content: flex-end;
    align-items: center;
    position: relative;
    &:hover {
        cursor: pointer;
        position: relative;
        &::before {
            content: '';
            position: absolute;
            z-index: 2;
            right: -5px;
            width: 32px;
            height: 32px;
            background-color: ${palette.indigo[1]};
            border-radius: 20px;
        }
    }
    & svg {
        font-size: 22px;
        z-index: 2;
        fill: #718096;
    }
`;

const ChannelNameWrapper = styled.div`
    padding: 24px 12px 24px 0;
    overflow-wrap: break-word;
`;

class StatisticsChannels extends PureComponent<Props, ChannelsState> {
    public state: ChannelsState = {
        unreadyToRender: true, // store can be full of data, this prevents from displaing before clearing
        channelStatisticsValues: 'number',
        tabValue: 1,
        selectedChannels: [],
        exportOptions: {
            channelNames: [],
            fileName: null,
            compressionFormat: CompressionFormat.Zip,
            fileFormat: ExportFileFormats.Csv,
        },
        selectedAllCheckboxes: false,
    };

    private fileNameRef = React.createRef<HTMLInputElement>();

    public componentDidMount(): void {
        const { LoadChannelsStart, location } = this.props;

        const query: any = parseUrlParams(location.search);
        const params: ChannelsPayload = {};

        if (!isEmpty(query)) {
            if (query.page) {
                params.activePage = parseInt(query.page);
            }

            if (query.search) {
                params.search = query.search;
            }

            if (query.perPage) {
                params.perPage = parseInt(query.perPage);
            }
        }

        this.setState({ unreadyToRender: false }, () => {
            LoadChannelsStart(params);
        });
    }

    private changeExportFileFormat(format: ExportFileFormats): void {
        const { exportOptions } = this.state;

        exportOptions.fileFormat = format;

        this.setState({ exportOptions });
    }

    private changeExportCompression(enabled: boolean): void {
        const { exportOptions } = this.state;

        exportOptions.compressionFormat = enabled ? CompressionFormat.Zip : CompressionFormat.None;

        this.setState({ exportOptions });
    }

    private exportChannels(): void {
        const { ExportChannels } = this.props;
        const { selectedChannels, selectedAllCheckboxes, exportOptions } = this.state;
        const fileName = this.fileNameRef.current.value || 'export';
        const fileFormat: ExportFileFormats = exportOptions.fileFormat;

        const exportParams: ExportChannelsParams = {
            fileName,
            fileFormat,
            channelNames: selectedChannels,
            compressionFormat: exportOptions.compressionFormat,
        };

        if (selectedAllCheckboxes) {
            exportParams.allChannels = true;
            exportParams.channelNames = null;
        }

        ExportChannels(exportParams);
    }

    private openExportChannelsModal(): void {
        const { ShowDialog, t } = this.props;

        ShowDialog({
            title: t('exportchannels'),
            body: (
                <ExportModal
                    onCompressionChange={this.changeExportCompression.bind(this)}
                    onFileFormatChange={this.changeExportFileFormat.bind(this)}
                    inputRef={this.fileNameRef}
                />
            ),
            confirmLabel: t('export'),
            cancelLabel: t('cancel'),
            onClickConfirm: this.exportChannels.bind(this),
        });
    }

    private renderTable(): JSX.Element {
        if (this.state.unreadyToRender) {
            return <></>;
        }

        const isNumeric = this.state.channelStatisticsValues === 'number';
        const { channels, t } = this.props;

        const columnsWidth: Record<string, string> = {
            channelName: '20%',
            delivered: '10%',
            opened: '9%',
            clicked: '9%',
            bounced: '9%',
            complaints: '12%',
            unsubscribed: '12%',
            view: '7%',
        };

        const selectedAllCount = this.state.selectedAllCheckboxes ? channels.totalCount : null;

        return (
            <CustomTable
                rows={isNumeric ? this.getRowsWithNumbers() : this.getRowsWithPercents()}
                headers={this.getHeaders()}
                onSelectRow={this.onSelectChannel.bind(this)}
                columnsWidth={columnsWidth}
                loading={channels.loading}
                isEmpty={!channels?.loading && !+channels?.totalCount}
                emptyRowsNo={channels?.perPage}
                totalCount={selectedAllCount}
                stickyHeader={true}
                disableFirstRowBorderTop={true}
                lettersLimit={35}
                rowPadding={48}
                emptyStateProps={{ text: t('emptystate') }}
            />
        );
    }

    private getMobileTableRows(): any[] {
        const { channelsPreview, loading } = this.props.channels;
        const isNumeric = this.state.channelStatisticsValues === 'number';

        if (!channelsPreview) {
            return [];
        }

        if (loading) {
            const SkeletonTemplate = {
                key: 'skeleton',
                channelName: <Skeleton style={{ margin: ' 10px 0px' }} />,
                delivered: <Skeleton />,
                opened: <Skeleton />,
                clicked: <Skeleton />,
                bounced: <Skeleton />,
                complaints: <Skeleton />,
                unsubscribed: <Skeleton />,
                view: <Skeleton />,
            };

            return [...new Array(this.props.channels.perPage)].map((_) => SkeletonTemplate);
        }

        return channelsPreview.map((channel) => {
            return {
                Channel: channel.Name,
                Delivered: isNumeric ? channel.SentCount : `${channel.DeliveredPercentage}%`,
                Opened: isNumeric ? channel.OpenedCount : `${channel.OpenedPercentage}%`,
                Clicked: isNumeric ? channel.ClickedCount : `${channel.ClickedPercentage}%`,
                Bounced: isNumeric ? channel.FailedCount : `${channel.FailedPercentage}%`,
                Complaints: isNumeric ? channel.FailedAbuse : `${channel.FailedAbusePercentage}%`,
                Unsubscribed: isNumeric ? channel.UnsubscribedCount : `${channel.UnsubscribedPercentage}%`,
            };
        });
    }

    private getMobileTableHeaders(): string[] {
        const { t } = this.props;

        return [t('channel'), t('delivered'), t('opened'), t('clicked'), t('bounced'), t('complaints'), t('unsubscribed')];
    }

    private renderMobileTables(): JSX.Element {
        const { channels, t, Push } = this.props;

        if (!channels.loading && !channels.totalCount) {
            return (
                <Paper padding={{ top: 0, bottom: 0, left: 16, right: 16 }}>
                    <EmptyState text={t('emptystate')} />
                </Paper>
            );
        }

        const header = this.getMobileTableHeaders();
        const rows = this.getMobileTableRows();

        return (
            <MobileTableContainer>
                <MobileTable
                    headers={header}
                    rows={rows}
                    onClickButton={(e, rows): void => {
                        Push(
                            StatisticsPaths.StatisticsChannelOverview.replace(
                                pathsParams.channelName,
                                safelyEncodeBase64Url(rows.Channel as string)
                            )
                        );
                    }}
                    expandedRows={
                        {
                            channel: true,
                        } as any
                    }
                />
            </MobileTableContainer>
        );
    }

    private getHeaders(): CustomTableHeadersProps[] {
        const {
            t,
            account: { GeneralInfo },
        } = this.props;
        const { selectedChannels } = this.state;

        const canSeeExport = GeneralInfo?.AccountAccessLevel?.includes(AccessLevel.Export);

        if (selectedChannels.length > 0) {
            return [
                {
                    id: 'channelName',
                    numeric: false,
                    label: `${t('channels')}`,
                },
                {
                    id: 'delivered',
                    numeric: false,
                    label: '',
                    columnAlign: 'right' as any,
                },
                {
                    id: 'opened',
                    numeric: false,
                    label: '',
                    columnAlign: 'right' as any,
                },
                {
                    id: 'clicked',
                    numeric: false,
                    label: '',
                    columnAlign: 'right' as any,
                },
                {
                    id: 'bounced',
                    numeric: false,
                    label: '',
                    columnAlign: 'right' as any,
                },
                {
                    id: 'complaints',
                    numeric: false,
                    label: '',
                    columnAlign: 'right' as any,
                },
                {
                    id: 'unsubscribed',
                    numeric: true,
                    label: '',
                    columnAlign: 'right' as any,
                },
                {
                    id: 'view',
                    numeric: true,
                    label: '',
                    filterIcon: canSeeExport && (
                        <TertiaryButton
                            style={{ margin: 0, color: palette.indigo[6] }}
                            text={t('export')}
                            onClick={this.openExportChannelsModal.bind(this)}
                        ></TertiaryButton>
                    ),
                    headerAlign: 'right' as any,
                    columnAlign: 'right' as any,
                },
            ];
        }

        return [
            { id: 'channelName', numeric: false, label: t('channels'), sortable: false },
            { id: 'delivered', columnAlign: 'right' as any, numeric: true, label: t('delivered') },
            { id: 'opened', columnAlign: 'right' as any, numeric: true, label: t('opened') },
            { id: 'clicked', columnAlign: 'right' as any, numeric: true, label: t('clicked') },
            { id: 'bounced', columnAlign: 'right' as any, numeric: true, label: t('bounced') },
            { id: 'complaints', columnAlign: 'right' as any, numeric: true, label: t('complaints') },
            {
                id: 'unsubscribed',
                columnAlign: 'right' as any,
                numeric: true,
                label: t('unsubscribed'),
            },
            { id: 'view', columnAlign: 'right' as any, numeric: true, label: '' },
        ];
    }

    private getRowsWithNumbers(): any[] {
        const {
            channels: { channelsPreview },
        } = this.props;

        if (isEmpty(channelsPreview)) {
            return [];
        }

        return channelsPreview.map((channel) => {
            return {
                channelName: (
                    <ChannelNameWrapper>
                        <Typography variant="caption">{channel.Name}</Typography>
                    </ChannelNameWrapper>
                ),
                delivered: <Typography variant="caption">{channel.SentCount}</Typography>,
                opened: <Typography variant="caption">{channel.OpenedCount}</Typography>,
                clicked: <Typography variant="caption">{channel.ClickedCount}</Typography>,
                bounced: <Typography variant="caption">{channel.FailedCount}</Typography>,
                complaints: <Typography variant="caption">{channel.FailedAbuse}</Typography>,
                unsubscribed: <Typography variant="caption">{channel.UnsubscribedCount}</Typography>,
                view: <Typography variant="caption">{this.showChannelDetails(channel.Name)}</Typography>,
                rowID: channel.Name,
            };
        });
    }

    private getRowsWithPercents(): any[] {
        const {
            channels: { channelsPreview },
        } = this.props;

        if (isEmpty(channelsPreview)) {
            return [];
        }

        return channelsPreview.map((channel) => {
            return {
                channelName: (
                    <ChannelNameWrapper>
                        <Typography variant="caption">{channel.Name}</Typography>
                    </ChannelNameWrapper>
                ),
                delivered: <Typography variant="caption">{`${channel.DeliveredPercentage}%`}</Typography>,
                opened: <Typography variant="caption">{`${channel.OpenedPercentage}%`}</Typography>,
                clicked: <Typography variant="caption">{`${channel.ClickedPercentage}%`}</Typography>,
                bounced: <Typography variant="caption">{`${channel.FailedPercentage}%`}</Typography>,
                complaints: <Typography variant="caption">{`${channel.FailedAbusePercentage}%`}</Typography>,
                unsubscribed: <Typography variant="caption">{`${channel.UnsubscribedPercentage}%`}</Typography>,
                view: <Typography variant="caption">{this.showChannelDetails(channel.Name)}</Typography>,
                rowID: channel.Name,
            };
        });
    }

    private showChannelDetails(channelName): JSX.Element {
        const { Push } = this.props;

        return (
            <StyledVisibilityIcon>
                <VisibilityIcon
                    onClick={(e): void => {
                        e.stopPropagation();
                        Push(
                            StatisticsPaths.StatisticsChannelOverview.replace(pathsParams.channelName, safelyEncodeBase64Url(channelName))
                        );
                    }}
                />
            </StyledVisibilityIcon>
        );
    }

    private statisticsButtons(): JSX.Element {
        const toggleBtnOptions: ChannelsToggleBtnOptInterface[] = [
            {
                label: '#',
                value: ChannelsToggleBtnValues.Number,
            },
            {
                label: '%',
                value: ChannelsToggleBtnValues.Percent,
            },
        ];

        return (
            <ToggleButtonWrapper>
                <ToggleButtonGroup
                    defaultValue={ChannelsToggleBtnValues.Number}
                    size="small"
                    options={toggleBtnOptions}
                    onChange={(format) => {
                        this.setState({
                            channelStatisticsValues: format,
                        });
                    }}
                />
            </ToggleButtonWrapper>
        );
    }

    private changePage(page: number, perPage: number): void {
        const { LoadChannelsStart } = this.props;
        const { location } = this.props;
        const queryParams = parse(location.search);

        this.setState({ selectedChannels: [] }, (): void =>
            LoadChannelsStart({ activePage: page, perPage, search: queryParams.search as string })
        );
    }

    private findChannel(ev: ChangeEvent<HTMLInputElement>): void {
        const { FindChannels, channels } = this.props;

        this.setState({ selectedChannels: [] }, (): void => FindChannels({ search: ev?.target?.value || '', perPage: channels.perPage }));
    }

    private onSelectChannel(event: SyntheticEvent<HTMLInputElement>, selectedChannels: string[], selectedAll: boolean): void {
        this.setState({ selectedAllCheckboxes: selectedAll, selectedChannels });
    }

    private searchInput(): JSX.Element {
        const { t, location } = this.props;
        const queryParams = parse(location.search);

        return (
            <SearchInput
                id="channelsSearchInput"
                name="searchInput"
                defaultValue={queryParams.search}
                onChange={this.findChannel.bind(this)}
                onDelete={this.findChannel.bind(this)}
                placeholder={t('searchforchannels')}
                type="text"
            />
        );
    }

    private mobileFiltersApply(filtersObj): void {
        const newTableBtnValue = filtersObj[0];

        //check Value range tab
        if (newTableBtnValue !== this.state.channelStatisticsValues) {
            this.setState({ channelStatisticsValues: newTableBtnValue });
        }
    }

    private renderFooter(): JSX.Element {
        const { channels } = this.props;
        const { totalCount } = channels;

        return (
            <Pagination
                dataLength={totalCount}
                perPage={channels.perPage}
                activePage={channels.activePage}
                siblingCount={1}
                boundaryCount={1}
                isRecalculatingActivePage
                handlePaginationChange={this.changePage.bind(this)}
                perPageOptions={[
                    { label: '5', value: 5 },
                    { label: '10', value: 10 },
                    { label: '15', value: 15 },
                    { label: '20', value: 20 },
                    { label: '25', value: 25 },
                ]}
            />
        );
    }

    private renderLoadingState(): JSX.Element {
        const displayContainer = {
            display: 'flex',
            height: 'calc(100vh - 200px)',
        };

        return (
            <Container style={displayContainer}>
                <LoadingWrapper>
                    <SimpleLoader />
                </LoadingWrapper>
            </Container>
        );
    }

    private isMobile(): boolean {
        const { browser } = this.props;
        return browser?.lessThan?.hd;
    }

    private counter = 0;

    public render(): JSX.Element {
        const { t, channels } = this.props;
        const isMobile = this.isMobile();
        const { totalCount } = channels;

        if (!isMobile) {
            return (
                <Content>
                    <Header>
                        {this.statisticsButtons()}
                        <SearchContainer>{this.searchInput()}</SearchContainer>
                    </Header>
                    {this.renderTable()}
                    {!!totalCount && this.renderFooter()}
                </Content>
            );
        }

        if (channels?.loading && totalCount !== 0 && isEmpty(channels.channels)) {
            return this.renderLoadingState();
        }

        return (
            <ChannelsWrapper>
                <MobileHeader>
                    <MobileSearchContainer>{this.searchInput()}</MobileSearchContainer>
                    <FiltersContainer>
                        <FiltersPopover
                            onApply={(obj): void => {
                                this.mobileFiltersApply(obj);
                            }}
                            tabs={[
                                {
                                    label: t('valuerange'),
                                    defaultValue: 'number',
                                    options: [
                                        {
                                            label: t('count'),
                                            value: 'number',
                                        },
                                        {
                                            label: t('percentage'),
                                            value: 'percent',
                                        },
                                    ],
                                    type: 'radio',
                                },
                            ]}
                            title={t('filters')}
                            translations={{
                                apply: t('apply'),
                                close: t('close'),
                            }}
                        >
                            <FilterIcon color={palette.gray[6]} width={20} height={20} />
                        </FiltersPopover>
                    </FiltersContainer>
                </MobileHeader>
                <TableContainer>{this.renderMobileTables()}</TableContainer>
                {!!totalCount && <MobileFooter>{this.renderFooter()}</MobileFooter>}
            </ChannelsWrapper>
        );
    }
}

export default compose(withTranslation(), withRouter, connect<ChannelsProps, DispatchProps>(mapState, dispatchProps))(StatisticsChannels);
